import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import {
    IconElement,
    ImageElement,
    ProductService,
    FullImageElement
} from '../../../shared/services/catalog.service';
import {
    FileUploaderComponent,
    HcModal,
    HcSort,
    HcToastOptions,
    HcToasterService,
    ModalOptions,
    ModalService,
    PaginationComponent,
    SelectChangeEvent,
    SimpleModalOptions,
    TabChangeEvent
} from '@healthcatalyst/cashmere';
import { ChangeElement, ChangeService } from '../../../shared/services/change.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ServiceElement, ServiceService } from '../../../shared/services/service.service';
import { ComponentCanDeactivate } from '../../can-deactivate/component-can-deactivate';
import { OktaService } from '../../../shared/services/okta.service';
import { TypeElement, TypeService } from '../../../shared/services/type.service';
import { ProductLineElement, ProductLineService } from '../../../shared/services/product-line.service';
import { StatusElement, StatusService } from '../../../shared/services/status.service';
import { ItemService } from '../../../shared/services/item.service';
import { AdminChangeLogComponent } from '../../individual-change-log/admin-change-log.component';
import { DocumentService } from '../../../shared/services/document.service';
import { LightboxComponent } from '../../lightbox/lightbox.component';
import { environment } from '../../../../environments/environment';
import { solutionCapabilities } from '../../../shared/solutionCapabilities';

/** Admin product element editor */
@Component({
    selector: 'pd-admin-services-editor',
    templateUrl: './admin-services-editor.component.html',
    styleUrls: ['./admin-services-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminServicesEditorComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    modalRef: HcModal<TemplateRef<any>>;
    selectedTab = 0;
    service: ServiceElement;
    serviceLoading = true;
    serviceError = false;
    imagesEdited = false;
    serviceID: number;
    itemID: number;
    savedItem = -1;
    revisionDate = '';
    solutionCapabilitiesList = solutionCapabilities;
    serviceForm = new FormGroup({
        nameControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        previousNamesControl: new FormArray([new FormControl('', { nonNullable: true })]),
        IDControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        typeControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        productLineControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        statusControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        published: new FormControl(false, { nonNullable: true }),
        taglineControl: new FormControl('', { nonNullable: true }),
        problemControl: new FormControl('', { nonNullable: true }),
        approachControl: new FormControl('', { nonNullable: true }),
        benefitsFeaturesControl: new FormControl('', { nonNullable: true }),
        iconSVGControl: new FormControl('', { nonNullable: true }),
        iconPNGControl: new FormControl('', { nonNullable: true }),
        iconBase64Control: new FormControl('', { nonNullable: true }),
        techDescControl: new FormControl('', { nonNullable: true }),
        communityUrlControl: new FormControl('', { nonNullable: true }),
        shortDescription: new FormControl('', { nonNullable: true }),
        subtitle: new FormControl('', { nonNullable: true }),
        solutionArea: new FormControl(1, { nonNullable: true }),
        solutionCapability: new FormControl('', { nonNullable: true })
    });

    selectedImageFiles = new FormControl();
    imageForm = new FormGroup({});
    imgID = 0;
    images: FullImageElement[] = [];
    imagesToDelete: string[] = [];

    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean'], ['link']]
    };
    quillCSConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], ['clean'], ['link']]
    };
    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };

    types: TypeElement[] = [];
    productLines: ProductLineElement[] = [];
    statuses: StatusElement[] = [];

    saving = false;

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild('fileUploaderControl')
    fileUploaderControl: FileUploaderComponent;
    
    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    @ViewChild('changeLog')
    changeLog: AdminChangeLogComponent;

    get loading(): boolean {
        return this.serviceLoading;
    }

    get loadError(): boolean {
        return this.serviceError;
    }

    get serviceInputsDirty(): boolean {
        return this.serviceForm.dirty || this.imageForm.dirty || this.imagesEdited;
    }

    constructor(
        private route: ActivatedRoute,
        private productService: ProductService,
        private changeService: ChangeService,
        private toasterService: HcToasterService,
        private oktaService: OktaService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private itemService: ItemService,
        private serviceService: ServiceService,
        private modalService: ModalService,
        private documentService: DocumentService
    ) {
        super();
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            // If a service ID is supplied, we're editing an existing service
            if (params['id']) {
                this.serviceID = params['id'];
            } else {
                // Otherwise we're creating a new service so generate a new item ID for the service
                this.itemService.getNewID().pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {                            
                        this.itemID = data['itemNumber'];
                        this.serviceForm.controls['IDControl'].setValue(data['itemNumber']);
                        this.serviceError = false;
                        this.serviceLoading = false;
                    },
                    error: msg => { 
                        console.log( msg );
                        this.serviceError = true;
                        this.serviceLoading = false;
                    }
                });
            }
        });

        this.typeService.catTypesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.types = this.sortAlphabetically(list);
                if (this.productLines.length !== 0 && this.statuses.length !== 0) {
                    this.serviceID && this.loadService();
                }
            } else {
                !this.typeService.loadFailed ? this.typeService.loadCatTypes('service') : this.serviceError = true;
            }
        });

        this.productLineService.productLineListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLines = this.sortAlphabetically(list);
                if (this.types.length !== 0 && this.statuses.length !== 0) {
                    this.serviceID && this.loadService();
                }
            } else {
                !this.productLineService.loadFailed ? this.productLineService.refresh() : this.serviceError = true;
            }
        });


        this.statusService.statusListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statuses = this.sortAlphabetically(list);
                if (this.types.length !== 0 && this.productLines.length !== 0) {
                    this.serviceID && this.loadService();
                }
            } else {
                !this.statusService.loadFailed ? this.statusService.refresh() : this.serviceError = true;
            }
        });
    }

    //alphabetical sort helper function
    sortAlphabetically(list: any[]) {
        return list.sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }


    //check if the Item ID is already in use and show an error on the form field if it is
    checkID( uploadModal?: TemplateRef<any> ): void {
        const currentID = this.serviceForm.controls['IDControl'].value!;

        if ( this.savedItem !== currentID ) {
            this.itemService.checkID(currentID).pipe(takeUntil(this.unsubscribe)).subscribe({
                next: data => {        
                    if ( data['itemExists'] ) {
                        this.serviceForm.controls['IDControl'].setErrors({ valid: false });
                    } else if( uploadModal ) {
                        this.onClickSave(uploadModal);
                    }
                },
                error: msg => { 
                    console.log( msg );
                }
            });
        } else if ( uploadModal ) {
            this.onClickSave(uploadModal);
        }
    }

    loadImages(imageArray: ImageElement[]) {
        let productImages: FullImageElement[] = [];
        imageArray.forEach((img: ImageElement) => {
            const placementVal = img.placement ? img.placement : (img.visibleInSheet ? 'Body' : 'Hidden');
            productImages.push({
                id: this.imgID,
                uploadID: img.id,
                type: img.type,
                caption: img.caption,
                fileName: img.fileName,
                visibleInSheet: img.visibleInSheet || false,
                uploadState: 2,
                placement: placementVal
            });

            this.imageForm.addControl('caption' + this.imgID, new FormControl(img.caption.length > 0 ? img.caption : ''));
            this.imageForm.addControl('placement' + this.imgID, new FormControl(placementVal));
            this.imgID++;
        });
        this.images = productImages;
    }

    checkPlacement( event: SelectChangeEvent, index: number ) {
        const placement = event.value;
        if ( placement !== 'Hidden' ) {
            for (let i = 0; i < this.images.length; i++) {
                if ( this.imageForm.get('placement' + this.images[i].id)!.value === placement && i !== index ) {
                    this.imageForm.get('placement' + this.images[i].id)!.setValue('Hidden');
                }
            }
        }
    }

    loadService(): void {
        this.serviceService
            .detail(this.serviceID)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: data => {
                    this.revisionDate = data[0].revisionDate;
                    this.itemID = data[0].item;
                    this.savedItem = data[0].item;

                    //parse previous names and icons strings from database
                    if (data[0].previousNames) {
                        data[0].previousNames = JSON.parse(data[0].previousNames);
                        //create form fields and patch previous names

                        //if the number of controls don't match the number of previous names, set up the controls
                        if (data[0].previousNames.length !== this.serviceForm.controls.previousNamesControl.controls.length || data[0].previousNames.length === 1) {
                            for (let i = 0; i < data[0].previousNames.length; i++) {
                                if (i === 0) {
                                    this.serviceForm.controls.previousNamesControl.controls[i].patchValue(data[0].previousNames[0]);
                                } else {
                                    this.serviceForm.controls.previousNamesControl.push((new FormControl(data[0].previousNames[i], { nonNullable: true })));
                                }
                            }
                        }
                    }

                    if (data[0].images) {
                        const imgData = JSON.parse(data[0].images);
                        this.loadImages(imgData);
                    }

                    if (data[0].icons) {
                        data.icons = JSON.parse(data[0].icons);
                    }

                    this.serviceError = false;
                    this.serviceLoading = false;

                    //patch for fields with technology data
                    this.serviceForm.patchValue({
                        nameControl: data[0].name,
                        IDControl: data[0].item,
                        typeControl: data[0].typeID,
                        productLineControl: data[0].productLineID,
                        statusControl: data[0].statusID,
                        published: data[0].published,
                        taglineControl: data[0].tagline,
                        problemControl: data[0].problem,
                        approachControl: data[0].approach,
                        benefitsFeaturesControl: data[0].features,
                        iconSVGControl: data[0].icons['svgURL'],
                        iconPNGControl: data[0].icons['pngURL'],
                        iconBase64Control: data[0].icons['base64URL'],
                        techDescControl: data[0].techDescription,
                        communityUrlControl: data[0].communityURL,
                        shortDescription: data[0].shortDescription,
                        subtitle: data[0].subtitle,
                        solutionArea: data[0].solutionArea ? data[0].solutionArea : 1,
                        solutionCapability: data[0].solutionCapability
                    });

                    this.service = {
                        serviceID: data[0].serviceID,
                        name: data[0].name,
                        item: data[0].item,
                        typeID: data[0].typeID,
                        productLineID: data[0].productLineID,
                        statusID: data[0].statusID,
                        published: data[0].published,
                        revisionDate: data[0].revisionDate,
                        tagline: data[0].tagline,
                        problem: data[0].problem,
                        approach: data[0].approach,
                        features: data[0].features,
                        images: data[0].images,
                        icons: data[0].icons,
                        techDescription: data[0].techDescription,
                        communityURL: data[0].communityURL,
                        previousNames: data[0].previousNames,
                        shortDescription: data[0].shortDescription,
                        subtitle: data[0].subtitle,
                        solutionArea: data[0].solutionArea,
                        solutionCapability: data.solutionCapability ? data.solutionCapability : this.solutionCapabilitiesList[(data.solutionArea ? data.solutionArea : 1)-1][0].id
                    };

                    this.changeLog.loadChanges(this.itemID, "service");

                    this.serviceForm.markAsPristine();
                },
                error: msg => {
                    this.serviceError = true;
                    this.serviceLoading = false;
                    console.log(msg);
                }
            });
    }

    ////Previous Name Functions////
    addPrevName() {
        this.serviceForm.controls.previousNamesControl.push(new FormControl('', { nonNullable: true }));
        this.serviceForm.controls.previousNamesControl.markAsDirty();
    }

    removePrevName(index: number) {
        this.serviceForm.controls.previousNamesControl.removeAt(index);
        this.serviceForm.controls.previousNamesControl.markAsDirty();
    }
    ////End Previous Name Functions////

    ////Image Functions////
    //add images and corresponding form controls to images table
    addImages() {
        //check that all files are images
        let fileCheckFail = false;
        for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
            if (!this.selectedImageFiles.value[i].type.includes('image/')) {
                fileCheckFail = true;
                break;
            }
        }

        if (fileCheckFail) {
            this.selectedImageFiles.markAsTouched();
            this.selectedImageFiles.setErrors({ invalid: true });
        } else {
            for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
                // add form controls and update the array of files to save
                this.imageForm.addControl('caption' + this.imgID, new FormControl(''));
                this.imageForm.addControl('placement' + this.imgID, new FormControl('Hidden'));
                this.images.push({
                    id: this.imgID,
                    uploadID: '',
                    caption: '',
                    type: this.selectedImageFiles.value[i].type,
                    fileName: this.selectedImageFiles.value[i].name,
                    visibleInSheet: false,
                    data: this.selectedImageFiles.value[i],
                    uploadState: 0,
                    placement: 'Hidden'
                });
                this.imgID++;
            }

            this.imagesEdited = true;

            //clear selected images
            if (this.fileUploaderControl) {
                setTimeout(() => {
                    this.fileUploaderControl.reset();
                });
            }
            this.selectedImageFiles = new FormControl();
        }
    }

    //open confirm removal modal, if the user confirms then remove the image reference
    confirmRemoveImage(img: FullImageElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the image: <strong>' + img.fileName + '</strong>?<p>',
            headerText: 'Remove Image',
            confirmButtonText: 'Remove Image',
            icon: ''
        };
        this.modalService
            .confirmDestructive(options)
            .pipe(
                take(1),
                filter(confirmed => confirmed)
            )
            .subscribe(() => {
                this.removeImageRef(img);
            });
    }

    //remove the image from the list of images and add it to the array of images to delete if it was already uploaded
    removeImageRef(img: FullImageElement) {
        let imgIndex = -1;
        for (let i = 0; i < this.images.length; i++) {
            if (img.id === this.images[i].id) {
                imgIndex = i;
                break;
            }
        }
        if (imgIndex > -1) {
            this.images.splice(imgIndex, 1);
            if (img.uploadID) {
                this.imagesToDelete.push(img.uploadID);
            }
            this.imagesEdited = true;
        }
    }

    //upload images, one by one
    uploadNextImage(): void {
        // Check if there are any images left to upload
        let nextIndex = -1;
        for (let i = this.images.length - 1; i >= 0; i--) {
            if (!this.images[i].uploadID && this.images[i].uploadState !== -1 && this.images[i].uploadState !== 2) {
                nextIndex = i;
            }
        }

        if (nextIndex !== -1) {
            if (this.images[nextIndex].data) {
                this.images[nextIndex].uploadState = 1;
                this.documentService
                    .addImage(this.images[nextIndex].data!)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        data => {
                            this.images[nextIndex].uploadState = 2;
                            this.images[nextIndex].uploadID = data.location;
                            this.uploadNextImage();
                        },
                        error => {
                            this.images[nextIndex].uploadState = -1;
                            console.log(error);
                            this.uploadNextImage();
                        }
                    );
            } else {
                this.images[nextIndex].uploadState = -1;
                this.uploadNextImage();
            }
        }
    }

    //delete images from blob storage
    deleteImages(): void {
        if (this.imagesToDelete.length > 0) {
            this.imagesToDelete.forEach((element, index) => {
                this.documentService
                    .removeImage(element)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        () => { },
                        error => {
                            console.log(error);
                        }
                    );
            });
        }
    }
    ////End Image Functions////

    //when a user clicks save, delete images and upload unsaved images OR save the technology
    onClickSave(imgUploadModal: TemplateRef<any>) {
        this.saving = true;
        const options: HcToastOptions = {
            header: 'Save Started',
            body: 'Your changes are being saved and synced. This may take several seconds.',
            type: 'info',
            position: 'bottom-right'
        };
        this.toasterService.addToast(options);

        this.deleteImages();

        if (this.images.length > 0) {
            //make list of images that are not already saved
            let unsavedImages: FullImageElement[] = [];
            this.images.forEach(element => {
                if (element.uploadState != 2) {
                    unsavedImages.push(element);
                }
            });

            //if there are unsaved images, save them
            if (unsavedImages.length > 0) {
                const options: ModalOptions = {
                    size: 'lg',
                    ignoreOverlayClick: true,
                    ignoreEscapeKey: true,
                    data: { images: unsavedImages }
                };

                //open upload progress modal
                this.modalRef = this.modalService.open(imgUploadModal, options);
                this.uploadNextImage();
            } else {
                this.saveService();
            }
        } else {
            this.saveService();
        }
    }
    
    //// Check for changes in all technology attributes and log them ////
    logChanges(service: ServiceElement) {
        let changes: ChangeElement[] = [];
        const date = new Date().toISOString();

        //check if name changed
        if (this.service.name !== service.name) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Name',
                previous: this.service.name,
                update: service.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to previous names////
        //check for changes in previous names values
        const prevNamesObj: string[] = typeof this.service.previousNames === 'object' ? this.service.previousNames : JSON.parse(this.service.previousNames);

        const prevStr = JSON.stringify(prevNamesObj);
        const updateStr = JSON.stringify(service.previousNames);

        //if there's an previous name change, add it
        if (prevStr !== updateStr) {
            const prevNameChange = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Previous Names',
                previous: prevStr,
                update: updateStr,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(prevNameChange);
        }
        ////End checks for changes to previous names////

        //check if item changed
        if (this.service.item !== service.item) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Item ID',
                previous: this.service.item.toString(),
                update: service.item.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if type changed
        if (this.service.typeID !== service.typeID) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Type',
                previous: this.service.typeID.toString(),
                update: service.typeID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if product line changed
        if (this.service.productLineID !== service.productLineID) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Product Line',
                previous: this.service.productLineID.toString(),
                update: service.productLineID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if status changed
        if (this.service.statusID !== service.statusID) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Status',
                previous: this.service.statusID.toString(),
                update: service.statusID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if published status changed
        if (this.service.published !== service.published) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Published Status',
                previous: this.service.published.toString(),
                update: service.published.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if tagline changed
        if (this.service.tagline !== service.tagline) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Tagline',
                previous: this.service.tagline,
                update: service.tagline,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if problem changed
        if (this.service.problem !== service.problem) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Problem',
                previous: this.service.problem,
                update: service.problem,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if approach changed
        if (this.service.approach !== service.approach) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Approach',
                previous: this.service.approach,
                update: service.approach,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if features changed
        if (this.service.features !== service.features) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Benefits and Features',
                previous: this.service.features,
                update: service.features,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if short description changed
        if (this.service.shortDescription !== service.shortDescription) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Short Description',
                previous: this.service.shortDescription,
                update: service.shortDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if subtitle changed
        if (this.service.subtitle !== service.subtitle) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Subtitle',
                previous: this.service.subtitle,
                update: service.subtitle,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Area changed
        if (this.service.solutionArea !== service.solutionArea) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Solution Area',
                previous: this.service.solutionArea ? this.service.solutionArea.toString() : 'none',
                update: service.solutionArea.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Capability changed
        if (this.service.solutionCapability !== service.solutionCapability) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Solution Capability',
                previous: this.service.solutionCapability ? this.solutionCapabilitiesList.flat().find(c => c.id === this.service.solutionCapability)!.name : 'none',
                update: this.solutionCapabilitiesList.flat().find(c => c.id === service.solutionCapability)!.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to images////
        //check for changes in image attributes
        let imagesChanged = false;

        const prevImgObj: ImageElement[] = typeof this.service.images === 'object' ? this.service.images : JSON.parse(this.service.images);

        service.images.forEach(element => {
            for (let i = 0; i < prevImgObj.length; i++) {
                if (element.id === prevImgObj[i].id) {
                    if (element.fileName !== prevImgObj[i].fileName) {
                        imagesChanged = true;
                    }

                    if (element.visibleInSheet !== prevImgObj[i].visibleInSheet) {
                        imagesChanged = true;
                    }

                    if (element.caption !== prevImgObj[i].caption) {
                        imagesChanged = true;
                    }

                    if (element.type !== prevImgObj[i].type) {
                        imagesChanged = true;
                    }

                    if (element.placement !== prevImgObj[i].placement) {
                        imagesChanged = true;
                    }
                }
            }
        });

        // check for change in amount of images
        if (service.images.length !== prevImgObj.length) {
            imagesChanged = true;
        }

        //if there's an image change, add it
        if (imagesChanged) {
            const imageChange = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Images',
                previous: JSON.stringify(prevImgObj),
                update: JSON.stringify(service.images),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(imageChange);
        }
        ////End checks for changes to images////

        ////Checks for changes to icons////
        //check for changes in icon attributes
        let iconsChanged = false;

        if (this.service.icons.svgURL !== service.icons.svgURL) {
            iconsChanged = true;
        } else if (this.service.icons.pngURL !== service.icons.pngURL) {
            iconsChanged = true;
        } else if (this.service.icons.base64URL !== service.icons.base64URL) {
            iconsChanged = true;
        }

        if (iconsChanged) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Icons',
                previous: JSON.stringify(this.service.icons),
                update: JSON.stringify(service.icons),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }
        ////End checks for changes to icons////

        //check if techDescription changed
        if (this.service.techDescription !== service.techDescription) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Technical Description',
                previous: this.service.techDescription,
                update: service.techDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if communityURL changed
        if (this.service.communityURL !== service.communityURL) {
            const change = {
                item: service.item,
                name: service.name,
                category: 'service',
                field: 'Community URL',
                previous: this.service.communityURL,
                update: service.communityURL,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        if (changes.length > 0) {
            this.changeService.add(changes);
        }
    }

    saveService() {
        //grab update date
        const date = new Date().toISOString();

        //turn full image element into image element
        let finalImages: ImageElement[] = [];
        this.images &&
            this.images.forEach(img => {
                const captionControl = 'caption' + img.id;
                const placementControl = 'placement' + img.id;

                if (img.uploadID) {
                    finalImages.push({
                        id: img.uploadID,
                        fileName: img.fileName,
                        visibleInSheet: img.placement !== 'Hidden',
                        type: img.type,
                        caption: this.imageForm.get(captionControl)!.value,
                        placement: this.imageForm.get(placementControl)!.value
                    });
                }
            });
        
        //turn icon URLs into object
        let iconURLS: IconElement = {
            'svgURL': this.serviceForm.controls['iconSVGControl'].value,
            'pngURL': this.serviceForm.controls['iconPNGControl'].value,
            'base64URL': this.serviceForm.controls['iconBase64Control'].value,
        }

        //turn previous names into object
        let previousNames: string[] = [];
        for (let i = 0; i < this.serviceForm.controls.previousNamesControl.length; i++) {
            previousNames.push(this.serviceForm.controls.previousNamesControl.controls[i].value);
        }

        //create the service object to save
        const service: ServiceElement = {
            serviceID: this.serviceID || -1,
            name: this.serviceForm.controls['nameControl'].value,
            item: this.serviceForm.controls['IDControl'].value!,
            typeID: this.serviceForm.controls['typeControl'].value!,
            productLineID: this.serviceForm.controls['productLineControl'].value!,
            statusID: this.serviceForm.controls['statusControl'].value!,
            published: this.serviceForm.controls['published'].value,
            revisionDate: date,
            tagline: this.serviceForm.controls['taglineControl'].value,
            problem: this.serviceForm.controls['problemControl'].value,
            approach: this.serviceForm.controls['approachControl'].value,
            features: this.serviceForm.controls['benefitsFeaturesControl'].value,
            images: finalImages,
            icons: iconURLS,
            techDescription: this.serviceForm.controls['techDescControl'].value,
            communityURL: this.serviceForm.controls['communityUrlControl'].value,
            previousNames: previousNames,
            shortDescription: this.serviceForm.controls['shortDescription'].value,
            subtitle: this.serviceForm.controls['subtitle'].value,
            solutionArea: this.serviceForm.controls['solutionArea'].value,
            solutionCapability: this.serviceForm.controls['solutionCapability'].value
        };

        //if the service is being edited, update it, log changes, and load changes
        if (this.serviceID) {
            this.serviceService
                .update(service)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: () => {
                        this.logChanges(service);
                        this.serviceService.refresh();
                        this.productService.refresh();
                        this.changeLog.loadChanges(this.itemID, "service");
                        const options: HcToastOptions = {
                            header: 'Service Updated',
                            body: service.name + ' successfully updated.',
                            type: 'success',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);

                        //mark everything pristine, update service variable, and update revision date
                        this.serviceForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.imagesEdited = false;
                        this.service = service;
                        this.revisionDate = date;
                        this.saving = false;
                    },
                    error: msg => {
                        this.saving = false;
                        const options: HcToastOptions = {
                            header: 'Update Failed',
                            body: 'Unable to update service. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        } else {
            //if the service is being added, add it, log an add change, update the item ID, and load changes
            this.serviceService
                .add(service)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: result => {
                        this.changeService.add([
                            {
                                item: service.item,
                                name: service.name,
                                category: 'service',
                                field: '--',
                                previous: '--',
                                update: 'service added',
                                user: this.oktaService.username.value,
                                date: new Date().toISOString()
                            }
                        ]);
                        this.serviceService.refresh();
                        this.serviceID = result;
                        this.itemID = service.item;
                        this.savedItem = service.item;
                        this.changeLog.loadChanges(this.itemID, "service");

                        const options: HcToastOptions = {
                            header: 'Service Added',
                            body: service.name + ' successfully added.',
                            type: 'success',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);

                        //mark form pristine and update service and revision date variables
                        this.serviceForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.imagesEdited = false;
                        this.service = service;
                        this.revisionDate = date;
                        this.saving = false;
                    },
                    error: msg => {
                        this.saving = false;
                        const options: HcToastOptions = {
                            header: 'Add Failed',
                            body: 'Unable to add service. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        }
    }

    openLightbox(img: FullImageElement) {
        const options: ModalOptions = {
            size: 'auto',
            closeIcon: true,
            data: {
                img
            }
        };

        this.modalService.open(LightboxComponent, options);
    }

    openPdf(): void {
        window.open('/viewer?id=' + this.itemID.toString(), '_blank');
    }

    generateLink() {
        const link = environment.root.url + '/viewer?id=' + this.itemID.toString();
        navigator.clipboard.writeText(link);

        const options: HcToastOptions = {
            header: 'Copied pdf URL for ' + this.service.name,
            type: 'info',
            position: 'bottom-center'
        };
        this.toasterService.addToast(options);
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    tabClick(selected: TabChangeEvent) {
        //ensure that newest changes are loaded if a user just updated
        selected.index === 1 && this.changeLog.loadChanges(this.itemID, "service");
        this.selectedTab = selected.index;
    }

    canDeactivate(): boolean {
        return !this.serviceInputsDirty;
    }

    defaultSolutionCapability(): void {
        this.serviceForm.controls['solutionCapability'].setValue( this.solutionCapabilitiesList[this.serviceForm.controls['solutionArea'].value - 1][0].id );
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
